import { useState, type FC } from "react";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import Divider from "../ui/Divider";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Alert } from "../ui/alert";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axiosClient from "../../lib/axiosClient";
import { toast } from "sonner";
import { ksUserQK } from "../../lib/query-keys";

interface Props {
  userId: string;
  open: boolean;
  setOpen: (val: boolean) => void;
  onSubmit: (siret?: string) => void;
}

const DemanderSiretDialog: FC<Props> = ({ userId, open, setOpen, onSubmit }) => {
  const [siret, setSiret] = useState("");
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => axiosClient.post(`/users/${userId}/siret`, { siret }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ksUserQK] });
      onSubmit(siret);
      setOpen(false);
    },

    onError: (e) => {
      console.error(e);
      toast.error("Une erreur s'est produite, veuillez réessayer ultérieurement.");
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='max-w-[650px]'>
        <DialogHeader>
          <DialogTitle>SIRET</DialogTitle>
        </DialogHeader>
        {/* Body */}
        <Divider />
        <form onSubmit={handleSubmit}>
          <Alert className='text-sm bg-orange-50 border-orange-200'>
            <div className='text-orange-700 font-semibold mb-1'>Important</div>
            Votre numéro SIRET est requis pour l'attestation d'éco-contribution.
          </Alert>
          <div className='my-5'>
            <Input
              type='text'
              placeholder='Votre numéro SIRET'
              value={siret}
              onChange={(e) => setSiret(e.currentTarget.value)}
              required
            />
          </div>
          {/* Footer */}
          <DialogFooter>
            <div>
              <div className='flex flex-row gap-5 justify-end'>
                <DialogClose asChild>
                  <Button variant={"outline"}>Annuler</Button>
                </DialogClose>
                <Button loading={mutation.isPending} type='submit' disabled={siret.trim() === ""}>
                  Valider
                </Button>
              </div>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DemanderSiretDialog;
